import { useState } from "react";
import { api } from "../../services/apiClient";

const DAY_OPTIONS = [
  { key: "sun", label: "Sun" },
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
];

const DEFAULT_SCAN_SCHEDULES = [
  { id: "default", time: "23:00", days: ["all"], enabled: true },
];

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

// "23:00" -> "11:00 PM" for display in a friendlier <input type="time"> wrapper
function to12HourLabel(time24) {
  const [hStr, mStr] = (time24 || "00:00").split(":");
  let h = parseInt(hStr, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${mStr} ${ampm}`;
}

export default function SettingsModal({ settings = {}, onSave, onClose }) {
  const [loading, setLoading] = useState(false);

  const [s, setS] = useState({
    dailyLimit: 1800,
    monthlyLimitMin: 45000,
    monthlyLimitMax: 55000,
    weekdayLimitMin: 500,
    weekdayLimitMax: 3000,
    weekendLimitMin: 400,
    weekendLimitMax: 1200,
    dailyVatMin: 5,
    dailyVatMax: 10,
    maxDiscountPercent: 10,
    saleDocumentTitle: "Sale Slip",
    autoDriveFolderSale: "",
    autoDriveFolderPurchase: "",
    saleFolders: [],
    purchaseFolders: [],
    salesLedgerFolderId: "",
    salesLedgerFileName: "Sales Ledger.xlsx",
    purchaseLedgerFolderId: "",
    purchaseLedgerFileName: "Purchase Ledger.xlsx",
    scanSchedules: DEFAULT_SCAN_SCHEDULES,
    ...settings,
  });

  const updateField = (key, value) => {
    setS((p) => ({ ...p, [key]: value }));
  };

  const updateList = (key, index, value) => {
    const arr = [...(s[key] || [])];
    arr[index] = value;
    updateField(key, arr);
  };

  const addItem = (key) => {
    updateField(key, [...(s[key] || []), ""]);
  };

  const removeItem = (key, index) => {
    const arr = [...(s[key] || [])];
    arr.splice(index, 1);
    updateField(key, arr);
  };

  // --- Scan schedule helpers (multiple times/days a day the auto-scan can run) ---
  const schedules = s.scanSchedules && s.scanSchedules.length > 0 ? s.scanSchedules : DEFAULT_SCAN_SCHEDULES;

  const updateSchedule = (id, patch) => {
    updateField(
      "scanSchedules",
      schedules.map((sch) => (sch.id === id ? { ...sch, ...patch } : sch))
    );
  };

  const toggleScheduleDay = (id, dayKey) => {
    const sch = schedules.find((x) => x.id === id);
    if (!sch) return;
    const current = sch.days && sch.days.length > 0 ? sch.days : ["all"];

    let nextDays;
    if (dayKey === "all") {
      nextDays = ["all"];
    } else if (current.includes("all")) {
      nextDays = [dayKey];
    } else if (current.includes(dayKey)) {
      nextDays = current.filter((d) => d !== dayKey);
      if (nextDays.length === 0) nextDays = ["all"];
    } else {
      nextDays = [...current, dayKey];
    }
    updateSchedule(id, { days: nextDays });
  };

  const addSchedule = () => {
    updateField("scanSchedules", [
      ...schedules,
      { id: uid(), time: "23:00", days: ["all"], enabled: true },
    ]);
  };

  const removeSchedule = (id) => {
    const next = schedules.filter((sch) => sch.id !== id);
    updateField("scanSchedules", next.length > 0 ? next : DEFAULT_SCAN_SCHEDULES);
  };

  async function save() {
    try {
      setLoading(true);
  const cleanSaleFolders = (s.saleFolders || []).filter(
        (val) => val !== null && val !== undefined && String(val).trim() !== ""
      )
      const cleanPurchaseFolders = (s.purchaseFolders || []).filter(
        (val) => val !== null && val !== undefined && String(val).trim() !== ""
      )
      const cleanSchedules = schedules
        .filter((sch) => sch.time)
        .map((sch) => ({
          id: sch.id || uid(),
          time: sch.time,
          days: sch.days && sch.days.length > 0 ? sch.days : ["all"],
          enabled: sch.enabled !== false,
        }));

      await Promise.all([
        api.setSetting("dailyLimit", Number(s.dailyLimit || 0)),
        api.setSetting("monthlyLimitMin", Number(s.monthlyLimitMin || 0)),
        api.setSetting("monthlyLimitMax", Number(s.monthlyLimitMax || 0)),
        api.setSetting("weekdayLimitMin", Number(s.weekdayLimitMin || 0)),
        api.setSetting("weekdayLimitMax", Number(s.weekdayLimitMax || 0)),
        api.setSetting("weekendLimitMin", Number(s.weekendLimitMin || 0)),
        api.setSetting("weekendLimitMax", Number(s.weekendLimitMax || 0)),
        api.setSetting("dailyVatMin", Number(s.dailyVatMin || 0)),
        api.setSetting("dailyVatMax", Number(s.dailyVatMax || 0)),
        api.setSetting("maxDiscountPercent", Number(s.maxDiscountPercent !== undefined && s.maxDiscountPercent !== '' ? s.maxDiscountPercent : 10)),
        api.setSetting("saleDocumentTitle", s.saleDocumentTitle || "Sale Slip"),
        api.setSetting("autoDriveFolderSale", s.autoDriveFolderSale || ""),
        api.setSetting("autoDriveFolderPurchase", s.autoDriveFolderPurchase || ""),
         api.setSetting("saleFolders", cleanSaleFolders),
        api.setSetting("purchaseFolders", cleanPurchaseFolders),
        api.setSetting("scanSchedules", cleanSchedules),
        api.setSetting("salesLedgerFolderId", s.salesLedgerFolderId || ""),
        api.setSetting("salesLedgerFileName", s.salesLedgerFileName || "Sales Ledger.xlsx"),
        api.setSetting("purchaseLedgerFolderId", s.purchaseLedgerFolderId || ""),
        api.setSetting("purchaseLedgerFileName", s.purchaseLedgerFileName || "Purchase Ledger.xlsx"),
      ]);

      onSave?.({ ...s, scanSchedules: cleanSchedules });
      onClose?.();
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
    >
      <div
        className="modal-box"
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh', /* Prevents modal from leaving screen bounds */
          width: '100%',
          maxWidth: '500px',
          backgroundColor: '#fff',
          borderRadius: '8px',
          overflow: 'hidden' /* Cleans up rounded corner spills */
        }}
      >

        <div className="modal-head" style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
          <span className="modal-title">⚙ System Settings</span>
          <button className="btn btn-icon" onClick={onClose}>✕</button>
        </div>

        {/* Scroll Engine Block */}
        <div
          className="modal-body scroll-area"
          style={{
            flex: 1,
            overflowY: 'auto', /* Enables the vertical scrollbar */
            padding: '20px'
          }}
        >

          {/* <div className="alert alert-info">
            Settings are stored in MongoDB and restored automatically.
          </div> */}

          <div className="settings-label" style={{ fontWeight: '600', marginBottom: '4px' }}>
            Sales Limits
          </div>
          <div className="settings-hint" style={{ color: '#94a3b8', marginBottom: '12px', fontSize: 12.5 }}>
            Auto-fill tops daily sales up to meet the daily target range below (and never past the maximum).
            Daily targets adapt to help achieve the Monthly Target range across the month.
          </div>

          <div className="settings-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <div>
              <div className="settings-label">Monthly Target Range</div>
              <div className="settings-hint">e.g. 45000–55000</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                className="form-input"
                type="number"
                value={s.monthlyLimitMin}
                onChange={(e) => updateField("monthlyLimitMin", e.target.value)}
                style={{ width: 100, textAlign: "right" }}
              />
              <span style={{ color: '#94a3b8' }}>–</span>
              <input
                className="form-input"
                type="number"
                value={s.monthlyLimitMax}
                onChange={(e) => updateField("monthlyLimitMax", e.target.value)}
                style={{ width: 100, textAlign: "right" }}
              />
            </div>
          </div>

          <div className="settings-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <div>
              <div className="settings-label">Weekday Range (Mon–Fri)</div>
              <div className="settings-hint">e.g. 500–3000</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                className="form-input"
                type="number"
                value={s.weekdayLimitMin}
                onChange={(e) => updateField("weekdayLimitMin", e.target.value)}
                style={{ width: 100, textAlign: "right" }}
              />
              <span style={{ color: '#94a3b8' }}>–</span>
              <input
                className="form-input"
                type="number"
                value={s.weekdayLimitMax}
                onChange={(e) => updateField("weekdayLimitMax", e.target.value)}
                style={{ width: 100, textAlign: "right" }}
              />
            </div>
          </div>

          <div className="settings-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <div className="settings-label">Weekend Range (Sat–Sun)</div>
              <div className="settings-hint">e.g. 400–1200</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                className="form-input"
                type="number"
                value={s.weekendLimitMin}
                onChange={(e) => updateField("weekendLimitMin", e.target.value)}
                style={{ width: 100, textAlign: "right" }}
              />
              <span style={{ color: '#94a3b8' }}>–</span>
              <input
                className="form-input"
                type="number"
                value={s.weekendLimitMax}
                onChange={(e) => updateField("weekendLimitMax", e.target.value)}
                style={{ width: 100, textAlign: "right" }}
              />
            </div>
          </div>

          <div className="settings-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <div className="settings-label">Daily VAT Range (£)</div>
              <div className="settings-hint">Total VAT in pounds (£) for the whole invoice, e.g. 5–10</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                className="form-input"
                type="number"
                value={s.dailyVatMin}
                onChange={(e) => updateField("dailyVatMin", e.target.value)}
                style={{ width: 100, textAlign: "right" }}
              />
              <span style={{ color: '#94a3b8' }}>–</span>
              <input
                className="form-input"
                type="number"
                value={s.dailyVatMax}
                onChange={(e) => updateField("dailyVatMax", e.target.value)}
                style={{ width: 100, textAlign: "right" }}
              />
            </div>
          </div>

          <div className="settings-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <div className="settings-label">Max Receipt Discount (%)</div>
              <div className="settings-hint">Maximum discount percentage allowed when matching real receipt amounts (default 10%)</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                className="form-input"
                type="number"
                min="0"
                max="100"
                step="1"
                value={s.maxDiscountPercent !== undefined && s.maxDiscountPercent !== null ? s.maxDiscountPercent : 10}
                onChange={(e) => updateField("maxDiscountPercent", e.target.value)}
                style={{ width: 100, textAlign: "right" }}
              />
              <span style={{ color: '#94a3b8' }}>%</span>
            </div>
          </div>

          <div className="settings-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <div className="settings-label">Sale Document Title</div>
              <div className="settings-hint">Shown at the top of every sale invoice/slip (e.g. "Sale Slip")</div>
            </div>

            <input
              className="form-input"
              type="text"
              value={s.saleDocumentTitle}
              onChange={(e) => updateField("saleDocumentTitle", e.target.value)}
              style={{ width: 160, textAlign: "right" }}
            />
          </div>
  <div className="settings-label" style={{ marginTop: 20, fontWeight: '600', marginBottom: '4px' }}>
            Auto-Scan Schedule
          </div>
          <div className="settings-hint" style={{ color: '#94a3b8', marginBottom: '10px', fontSize: 12.5 }}>
            Choose any time(s) and day(s) the sale/purchase Drive scan should run automatically.
            Default is every day at 11:00 PM — add more slots to run it more than once a day.
          </div>

          {schedules.map((sch) => {
            const days = sch.days && sch.days.length > 0 ? sch.days : ["all"];
            const isAllDays = days.includes("all");
            return (
              <div
                key={sch.id}
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  padding: '12px',
                  marginBottom: 10,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={sch.enabled !== false}
                      onChange={(e) => updateSchedule(sch.id, { enabled: e.target.checked })}
                    />
                    Enabled
                  </label>

                 {(() => {
  const [hour = "23", minute = "00"] = (sch.time || "23:00").split(":");

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <select
        className="form-input"
        value={hour}
        style={{ width: 70 }}
        onChange={(e) =>
          updateSchedule(sch.id, {
            time: `${e.target.value}:${minute}`,
          })
        }
      >
        {Array.from({ length: 24 }, (_, i) => {
          const h = String(i).padStart(2, "0");
          return (
            <option key={h} value={h}>
              {h}
            </option>
          );
        })}
      </select>

      <span>:</span>

      <select
        className="form-input"
        value={minute}
        style={{ width: 70 }}
        onChange={(e) =>
          updateSchedule(sch.id, {
            time: `${hour}:${e.target.value}`,
          })
        }
      >
        {Array.from({ length: 60 }, (_, i) => {
          const m = String(i).padStart(2, "0");
          return (
            <option key={m} value={m}>
              {m}
            </option>
          );
        })}
      </select>
    </div>
  );
})()}

                  <span style={{ fontSize: 12, color: '#94a3b8' }}>
                    ({to12HourLabel(sch.time)})
                  </span>

                  <button
                    className="btn-danger"
                    onClick={() => removeSchedule(sch.id)}
                    style={{
                      marginLeft: 'auto',
                      width: 32,
                      height: 32,
                      border: 'none',
                      borderRadius: 6,
                      backgroundColor: '#fee2e2',
                      color: '#ef4444',
                      cursor: 'pointer',
                    }}
                    title="Remove this schedule"
                  >
                    ✕
                  </button>
                </div>

                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => toggleScheduleDay(sch.id, "all")}
                    className="btn btn-sm"
                    style={{
                      padding: '4px 10px',
                      borderRadius: 14,
                      border: '1px solid ' + (isAllDays ? '#6366f1' : '#e2e8f0'),
                      backgroundColor: isAllDays ? '#6366f1' : '#fff',
                      color: isAllDays ? '#fff' : '#334155',
                      cursor: 'pointer',
                      fontSize: 12,
                    }}
                  >
                    All days
                  </button>
                  {DAY_OPTIONS.map((d) => {
                    const active = !isAllDays && days.includes(d.key);
                    return (
                      <button
                        type="button"
                        key={d.key}
                        onClick={() => toggleScheduleDay(sch.id, d.key)}
                        className="btn btn-sm"
                        style={{
                          padding: '4px 10px',
                          borderRadius: 14,
                          border: '1px solid ' + (active ? '#6366f1' : '#e2e8f0'),
                          backgroundColor: active ? '#6366f1' : '#fff',
                          color: active ? '#fff' : '#334155',
                          cursor: 'pointer',
                          fontSize: 12,
                        }}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <button className="btn btn-ghost" onClick={addSchedule} style={{ cursor: 'pointer', marginBottom: '10px' }}>
            + Add Time Slot
          </button>
          <div className="settings-label" style={{ fontWeight: '600', marginBottom: '8px' }}>Google Drive Settings</div>

          <input
            className="form-input"
            placeholder="Sales Folder ID"
            value={s.autoDriveFolderSale}
            onChange={(e) =>
              updateField("autoDriveFolderSale", e.target.value)
            }
            style={{ marginBottom: 10, width: '100%' }}
          />

          <input
            className="form-input"
            placeholder="Purchase Folder ID"
            value={s.autoDriveFolderPurchase}
            onChange={(e) =>
              updateField("autoDriveFolderPurchase", e.target.value)
            }
            style={{ width: '100%' }}
          />

         
        

          <div className="settings-label" style={{ marginTop: 20, fontWeight: '600', marginBottom: '8px' }}>
            Sales Folders
          </div>

          {(s.saleFolders || []).length === 0 && (
            <div className="settings-empty" style={{ color: '#94a3b8', margin: '8px 0' }}>No folders added</div>
          )}

          {(s.saleFolders || []).map((val, i) => (
            <div className="settings-row-flex" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              marginBottom: '8px'
            }} key={i}>
              <input
                className="form-input"
                value={val}
                onChange={(e) =>
                  updateList("saleFolders", i, e.target.value)
                }
                style={{ flex: 1 }}
              />
              <button
                className="btn-danger"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '38px',
                  height: '38px',
                  flexShrink: 0,
                  backgroundColor: '#fee2e2',
                  color: '#ef4444',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#fecaca';
                  e.currentTarget.style.color = '#dc2626';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fee2e2';
                  e.currentTarget.style.color = '#ef4444';
                }}
                onClick={() => removeItem("saleFolders", i)}
              >
                ✕
              </button>
            </div>
          ))}

          <button className="btn btn-ghost" onClick={() => addItem("saleFolders")} style={{ cursor: 'pointer', marginBottom: '10px' }}>
            + Add Folder
          </button>

          <div className="settings-label" style={{ marginTop: 20, fontWeight: '600', marginBottom: '8px' }}>
            Purchase Folders
          </div>

          {(s.purchaseFolders || []).length === 0 && (
            <div className="settings-empty" style={{ color: '#94a3b8', margin: '8px 0' }}>No folders added</div>
          )}

          {(s.purchaseFolders || []).map((val, i) => (
            <div className="settings-row-flex" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              marginBottom: '8px'
            }} key={i}>
              <input
                className="form-input"
                value={val}
                onChange={(e) =>
                  updateList("purchaseFolders", i, e.target.value)
                }
                style={{ flex: 1 }}
              />
              <button
                className="btn-danger"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '38px',
                  height: '38px',
                  flexShrink: 0,
                  backgroundColor: '#fee2e2',
                  color: '#ef4444',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#fecaca';
                  e.currentTarget.style.color = '#dc2626';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fee2e2';
                  e.currentTarget.style.color = '#ef4444';
                }}
                onClick={() => removeItem("purchaseFolders", i)}
              >
                ✕
              </button>
            </div>
          ))}

          <button className="btn btn-ghost" onClick={() => addItem("purchaseFolders")} style={{ cursor: 'pointer' }}>
            + Add Folder
          </button>
 <div className="settings-label" style={{ marginTop: 20, fontWeight: '600', marginBottom: '4px' }}>
            Sales Ledger File
          </div>
          {/* <div className="settings-hint" style={{ color: '#94a3b8', marginBottom: '10px', fontSize: 12.5 }}>
            Every calculated sale is appended as a row to this master ledger file on Drive
            (a new monthly sheet is created automatically as needed).
          </div> */}
          <input
            className="form-input"
            placeholder="Sales Ledger Folder ID"
            value={s.salesLedgerFolderId}
            onChange={(e) => updateField("salesLedgerFolderId", e.target.value)}
            style={{ marginBottom: 10, width: '100%' }}
          />
          <input
            className="form-input"
            placeholder="Sales Ledger File Name (e.g. Sales Ledger.xlsx)"
            value={s.salesLedgerFileName}
            onChange={(e) => updateField("salesLedgerFileName", e.target.value)}
            style={{ width: '100%' }}
          />

          <div className="settings-label" style={{ marginTop: 20, fontWeight: '600', marginBottom: '4px' }}>
            Purchase Ledger File
          </div>
          {/* <div className="settings-hint" style={{ color: '#94a3b8', marginBottom: '10px', fontSize: 12.5 }}>
            Purchases no longer generate a separate invoice file — instead, each calculated
            purchase is appended as a row to this master ledger file on Drive.
          </div> */}
          <input
            className="form-input"
            placeholder="Purchase Ledger Folder ID"
            value={s.purchaseLedgerFolderId}
            onChange={(e) => updateField("purchaseLedgerFolderId", e.target.value)}
            style={{ marginBottom: 10, width: '100%' }}
          />
          <input
            className="form-input"
            placeholder="Purchase Ledger File Name (e.g. Purchase Ledger.xlsx)"
            value={s.purchaseLedgerFileName}
            onChange={(e) => updateField("purchaseLedgerFileName", e.target.value)}
            style={{ width: '100%' }}
          />

        </div>

        <div className="modal-foot" style={{ padding: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px', borderTop: '1px solid #f1f5f9' }}>
          <button className="btn btn-ghost" onClick={onClose} style={{ cursor: 'pointer' }}>
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={save}
            disabled={loading}
            style={{ cursor: 'pointer' }}
          >
            {loading ? "Saving..." : "Save Settings"}
          </button>
        </div>

      </div>
    </div>
  );
}
